import { gsap } from 'gsap';

const REDUCED_MOTION =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Card
 * ─────────────────────────────────────────────────────────────
 * One carousel card. Owns its own animation state — Carousel composes
 * the final transform each frame from these properties so multiple
 * sources (idle bob, hover lift, drag tilt, scroll parallax, entrance)
 * coexist without fighting each other for `transform`.
 *
 *   idleY    – continuous yoyo from the float tween
 *   hoverY   – additive bump on pointer enter
 *   dragRot  – rotation driven by carousel drag velocity
 *   scrollY  – parallax driven by Lenis scroll velocity
 *   enterY   – entrance animation offset
 *   opacity  – entrance fade
 *
 * The Carousel reads these and writes a composite transform per frame.
 */
export class Card {
  constructor(el, index) {
    this.el = el;
    this.index = index;
    this.world = el.dataset.world;

    // Animation state — read each frame by Carousel.applyTransform()
    this.idleY = 0;
    this.hoverY = 0;
    this.dragRot = 0;
    this.scrollY = 0;
    this.enterY = 24;     // pre-entrance offset
    this.opacity = 0;     // pre-entrance opacity

    this._hoverTween = null;
    this._dragRotTween = null;
    this._enterTween = null;
    this._floatTween = null;

    el.addEventListener('pointerenter', this._onEnter);
    el.addEventListener('pointerleave', this._onLeave);

    this._startIdleFloat();
  }

  // ── public API used by Carousel ────────────────────────────

  /**
   * Trigger the entrance animation. Called by Carousel when the
   * section first enters the viewport (IntersectionObserver).
   */
  enter({ delay = 0 } = {}) {
    this._enterTween?.kill();
    this._enterTween = gsap.to(this, {
      enterY: 0,
      opacity: 1,
      duration: 0.8,
      ease: 'power3.out',
      delay: delay + this.index * 0.08,    // staggered glitch entrance
    });
  }

  /** Set drag-driven rotation. Updated every frame during a drag. */
  setDragRotation(deg) {
    // Cancel any in-flight ease-back if user starts dragging again
    if (this._dragRotTween && this._dragRotTween.isActive()) {
      this._dragRotTween.kill();
    }
    this.dragRot = deg;
  }

  /** Ease drag rotation back to 0 (called on pointerup). */
  releaseDragRotation() {
    this._dragRotTween?.kill();
    this._dragRotTween = gsap.to(this, {
      dragRot: 0,
      duration: 0.6,
      ease: 'power3.out',
    });
  }

  /** Set the parallax Y offset (Carousel decays this back to 0 in its tick). */
  setScrollOffset(y) {
    this.scrollY = y;
  }

  destroy() {
    this._hoverTween?.kill();
    this._dragRotTween?.kill();
    this._enterTween?.kill();
    this._floatTween?.kill();
    this.el.removeEventListener('pointerenter', this._onEnter);
    this.el.removeEventListener('pointerleave', this._onLeave);
  }

  // ── internal ────────────────────────────────────────────────

  _onEnter = () => {
    this.el.classList.add('is-hover');
    if (REDUCED_MOTION) return;
    this._hoverTween?.kill();
    this._hoverTween = gsap.to(this, {
      hoverY: -4,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  _onLeave = () => {
    this.el.classList.remove('is-hover');
    this._hoverTween?.kill();
    this._hoverTween = gsap.to(this, {
      hoverY: 0,
      duration: 0.4,
      ease: 'power3.out',
    });
  };

  _startIdleFloat() {
    if (REDUCED_MOTION) return;
    // Per-card phase via duration + delay so the row never feels mechanical.
    this._floatTween = gsap.to(this, {
      idleY: -6,
      duration: 2 + this.index * 0.3,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      delay: this.index * 0.15,
    });
  }
}
